/**
 * Service grouping. Automaid lets the tenant tag services with categories (`tags_info`, type
 * `service_category`); the vendor's own premium form filters on them. We group by those tags,
 * put untagged services under "Other", and fall back to a flat list when nothing is tagged.
 * Nothing here parses service names.
 */
import type { Service } from '../api/types';

export interface ServiceCategory {
  id: number;
  name: string;
  ordering: number;
  color: string | null;
  services: Service[];
}

export const OTHER_CATEGORY_ID = -1;
export const OTHER_CATEGORY_NAME = 'Other';
const CATEGORY_TAG_TYPE = 'service_category';

export function categorizeServices(services: Service[]): ServiceCategory[] {
  const byId = new Map<number, ServiceCategory>();
  const other: Service[] = [];
  for (const service of services) {
    const tags = (service.tags_info ?? []).filter((t) => typeof t.id === 'number' && (t.type ?? CATEGORY_TAG_TYPE) === CATEGORY_TAG_TYPE);
    if (!tags.length) {
      other.push(service);
      continue;
    }
    for (const tag of tags) {
      const id = tag.id!;
      const category = byId.get(id) ?? { id, name: tag.name?.trim() || `Category ${id}`, ordering: tag.ordering ?? 0, color: tag.color ?? null, services: [] };
      category.services.push(service);
      byId.set(id, category);
    }
  }
  if (!byId.size) return [];
  const list = [...byId.values()].sort((a, b) => a.ordering - b.ordering || a.name.localeCompare(b.name));
  if (other.length) list.push({ id: OTHER_CATEGORY_ID, name: OTHER_CATEGORY_NAME, ordering: Number.MAX_SAFE_INTEGER, color: null, services: other });
  return list;
}

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Resolve a `?vehicle=` value (slug of the category name, or its id) to a category. */
export function findCategory(categories: ServiceCategory[], key: string | null | undefined): ServiceCategory | null {
  if (!key) return null;
  const wanted = key.trim();
  if (!wanted) return null;
  const slug = slugify(wanted);
  return categories.find((c) => slugify(c.name) === slug) ?? categories.find((c) => String(c.id) === wanted) ?? null;
}

/**
 * Resolve a `?service=` value to a service: a numeric id, or a slug of the service name
 * (`stage-1-sedan`). Slugs are the form to use in links — ids differ between tenants.
 */
export function findService(services: Service[], key: string | null | undefined): Service | null {
  if (!key) return null;
  const wanted = key.trim();
  if (!wanted) return null;
  if (/^\d+$/.test(wanted)) return services.find((s) => s.id === Number(wanted)) ?? null;
  const slug = slugify(wanted);
  return services.find((s) => slugify(s.name ?? '') === slug) ?? null;
}

export function categoryOfService(categories: ServiceCategory[], serviceId: number | null | undefined): ServiceCategory | null {
  if (serviceId === null || serviceId === undefined) return null;
  return categories.find((c) => c.services.some((s) => s.id === serviceId)) ?? null;
}

/** What to print next to a service: its price, or its per-unit pricing parameter when the base price is 0. */
export function servicePriceLabel(service: Service, money: (n: number) => string): string | null {
  if (typeof service.price === 'number' && service.price > 0) return money(service.price);
  const param = (service.pricing_parameters ?? []).find((p) => typeof p.price === 'number' && p.price > 0);
  if (param) return `${money(param.price!)} ${param.name?.trim() ?? ''}`.trim();
  return null;
}
